import { z } from 'zod';
import { ADJUSTMENT_NAMES, REAL_FEED_NAMES } from './marketdata.js';
/**
 * The operations console's API boundary (A5): jobs, the ingest plan, instruments and
 * system status. Request schemas here are the HTTP-side validation; a job's `params` are
 * validated a second time by the job kind's own schema inside the closed registry, and the
 * kind itself is only ever a lookup key — never assembled into anything.
 */
export const JOB_STATUSES = [
    'queued',
    'running',
    'succeeded',
    'failed',
    'cancelling',
    'cancelled',
];
export const TERMINAL_JOB_STATUSES = ['succeeded', 'failed', 'cancelled'];
export const symbolSchema = z
    .string()
    .regex(/^[A-Z][A-Z0-9.]{0,9}$/, 'symbol must be 1–10 upper-case letters/digits/dots');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD');
/** POST /api/v1/jobs — `kind` is looked up in the registry or the request is rejected. */
export const enqueueJobBodySchema = z.object({
    kind: z.string().min(1).max(64),
    params: z.record(z.string(), z.unknown()).default({}),
});
export const listJobsQuerySchema = z.object({
    status: z.enum(JOB_STATUSES).optional(),
    kind: z.string().min(1).max(64).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const jobLogQuerySchema = z.object({
    after: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(2000).default(500),
});
export const jobSchema = z.object({
    id: z.string(),
    kind: z.string(),
    params: z.record(z.string(), z.unknown()),
    status: z.enum(JOB_STATUSES),
    concurrencyKey: z.string(),
    enqueuedBy: z.string().nullable(),
    enqueuedByEmail: z.string().nullable().optional(),
    enqueuedAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    leaseExpiresAt: z.string().nullable(),
    workerId: z.string().nullable(),
    attempt: z.number().int(),
    maxAttempts: z.number().int(),
    progressDone: z.number().int().nullable(),
    progressTotal: z.number().int().nullable(),
    progressNote: z.string().nullable(),
    result: z.unknown().nullable(),
    error: z.string().nullable(),
    endedBy: z.enum(['worker', 'operator', 'reaper']).nullable(),
    requeueReason: z.enum(['worker_shutdown', 'lease_expired']).nullable(),
    reapedCount: z.number().int(),
    /** A6: the stored credential this job ran with, when one was used. */
    credentialId: z.string().nullable(),
    updatedAt: z.string(),
});
export const jobLogLineSchema = z.object({
    seq: z.number().int(),
    ts: z.string(),
    line: z.string(),
});
export const jobListResponseSchema = z.object({
    items: z.array(jobSchema),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
});
export const jobKindInfoSchema = z.object({
    kind: z.string(),
    title: z.string(),
    description: z.string(),
    needsCredentials: z.union([z.boolean(), z.literal('depends')]),
    paramsJsonSchema: z.record(z.string(), z.unknown()),
});
// ---- ingest plan ---------------------------------------------------------------
export const planIngestBodySchema = z
    .object({
    symbol: symbolSchema,
    from: isoDate,
    to: isoDate,
    feed: z.enum(REAL_FEED_NAMES).default('iex'),
    adjustment: z.enum(ADJUSTMENT_NAMES).default('split'),
    threshold: z.coerce.number().min(0.5).max(1).default(0.95),
    chunkDays: z.coerce.number().int().min(1).max(60).default(10),
    force: z.coerce.boolean().default(false),
})
    .refine((p) => p.from <= p.to, { message: 'from must not be after to', path: ['to'] });
export const plannedSessionSchema = z.object({
    date: isoDate,
    action: z.enum(['fetch', 'skip', 'refetch']),
    reason: z.string(),
    existingCoverage: z.number().nullable(),
    exhausted: z.object({ attempts: z.number().int(), lastFetchedAt: z.string() }).optional(),
});
export const ingestPlanSchema = z.object({
    symbol: z.string(),
    feed: z.string(),
    adjustment: z.string(),
    from: isoDate,
    to: isoDate,
    threshold: z.number(),
    chunkDays: z.number().int(),
    force: z.boolean(),
    sessions: z.array(plannedSessionSchema),
    summary: z.object({
        fetch: z.number().int(),
        skip: z.number().int(),
        refetch: z.number().int(),
        exhausted: z.number().int(),
        estimatedRequests: z.number().int(),
        chunks: z.number().int(),
    }),
    toFetch: z.array(isoDate),
    calendarSources: z.array(z.string()),
    cursor: z.object({ ts: z.string().nullable(), disagreements: z.array(isoDate) }),
});
// ---- instruments ---------------------------------------------------------------
export const INSTRUMENT_ROLES = ['labeled', 'context'];
export const instrumentsQuerySchema = z.object({
    feed: z.enum(REAL_FEED_NAMES).default('iex'),
    adjustment: z.enum(ADJUSTMENT_NAMES).default('split'),
    threshold: z.coerce.number().min(0.5).max(1).default(0.95),
});
export const createInstrumentBodySchema = z.object({
    symbol: symbolSchema,
    role: z.enum(INSTRUMENT_ROLES).default('labeled'),
    notes: z.string().max(500).nullable().optional(),
});
export const patchInstrumentBodySchema = z
    .object({
    enabled: z.boolean().optional(),
    role: z.enum(INSTRUMENT_ROLES).optional(),
    notes: z.string().max(500).nullable().optional(),
})
    .refine((b) => Object.keys(b).length > 0, { message: 'nothing to change' });
/** One row of the Instruments status table, for one (feed, adjustment, threshold). */
export const instrumentStatusSchema = z.object({
    symbol: symbolSchema,
    role: z.enum(INSTRUMENT_ROLES),
    enabled: z.boolean(),
    notes: z.string().nullable(),
    addedAt: z.string(),
    updatedAt: z.string(),
    feed: z.enum(REAL_FEED_NAMES),
    adjustment: z.enum(ADJUSTMENT_NAMES),
    /** Sessions with any bars: first and last date, or null when nothing is loaded. */
    firstSession: isoDate.nullable(),
    lastSession: isoDate.nullable(),
    /** Calendar sessions between first and last (inclusive) vs sessions that have bars. */
    sessionsExpected: z.number().int(),
    sessionsPresent: z.number().int(),
    /** numeric aggregates as decimal strings, never JS numbers. */
    meanCoverage: z.string().nullable(),
    minCoverage: z.string().nullable(),
    belowThreshold: z.number().int(),
    /** bars_1m rows for this series. */
    bars: z.number().int(),
    lastJob: jobSchema
        .pick({ id: true, kind: true, status: true, enqueuedAt: true, finishedAt: true })
        .nullable(),
});
export const instrumentsResponseSchema = z.object({
    feed: z.enum(REAL_FEED_NAMES),
    adjustment: z.enum(ADJUSTMENT_NAMES),
    threshold: z.number(),
    items: z.array(instrumentStatusSchema),
});
// ---- system status -------------------------------------------------------------
export const WORKER_STATES = ['unknown', 'healthy', 'stale', 'down'];
export const systemStatusSchema = z.object({
    api: z.object({ version: z.string(), now: z.string() }),
    worker: z.object({
        state: z.enum(WORKER_STATES),
        lastSeen: z.string().nullable(),
        workers: z.array(z.object({
            id: z.string(),
            version: z.string().nullable(),
            lastSeenAt: z.string(),
            runningJobId: z.string().nullable(),
            /** The intervals this worker runs with — it reports them on every heartbeat. */
            heartbeatSeconds: z.number().int(),
            leaseSeconds: z.number().int(),
            state: z.enum(['healthy', 'stale', 'down']),
        })),
    }),
    jobs: z.object({ running: z.number().int(), queued: z.number().int() }),
    /** Whether the jobs schema exists — false means the API has not migrated to 0005 yet. */
    migrated: z.boolean(),
});
// ---- external service credentials (A6) ----------------------------------------------------
export const CREDENTIAL_ORIGINS = ['database', 'environment', 'none'];
export const credentialSourceInfoSchema = z.object({
    origin: z.enum(CREDENTIAL_ORIGINS),
    detail: z.string(),
    credentialId: z.string().nullable(),
    version: z.number().int().nullable(),
    storedButUnreadable: z.boolean().optional(),
});
export const serviceFieldInfoSchema = z.object({
    name: z.string(),
    label: z.string(),
    secret: z.boolean(),
    help: z.string().optional(),
    schema: z.record(z.string(), z.unknown()),
});
/** A stored credential as the API shows it — there is no field for the secrets, by construction. */
export const credentialViewSchema = z.object({
    id: z.string(),
    serviceKey: z.string(),
    version: z.number().int(),
    config: z.record(z.string(), z.unknown()),
    hint: z.record(z.string(), z.string()),
    keyFingerprint: z.string(),
    createdBy: z.string().nullable(),
    createdByEmail: z.string().nullable(),
    createdAt: z.string(),
    revokedAt: z.string().nullable(),
    verifiedAt: z.string().nullable(),
    verifyStatus: z.enum(['pending', 'ok', 'failed']),
    verifyMessage: z.string().nullable(),
});
export const serviceStateSchema = z.object({
    key: z.string(),
    label: z.string(),
    description: z.string(),
    fields: z.array(serviceFieldInfoSchema),
    verifyJob: z.string().optional(),
    usedBy: z.array(z.string()),
    /** The active (non-revoked) version, if any. */
    active: credentialViewSchema.nullable(),
    /** Newest first, active included. */
    history: z.array(credentialViewSchema),
    /** Which source the WORKER would use right now, in words. */
    inUse: credentialSourceInfoSchema,
    /** The last credential.verify job for this service, if any. */
    lastVerifyJob: jobSchema
        .pick({ id: true, status: true, enqueuedAt: true, finishedAt: true })
        .nullable(),
});
export const servicesResponseSchema = z.object({
    /** Whether the API holds a public key — without it, writes are refused with a clear message. */
    canWrite: z.boolean(),
    publicKeyFingerprint: z.string().nullable(),
    items: z.array(serviceStateSchema),
});
/** POST /api/v1/services/:key/credential — every field the descriptor declares, secret and plain. */
export const writeCredentialBodySchema = z.record(z.string(), z.unknown());
//# sourceMappingURL=jobs.js.map